import { DEFAULT_OUTLINK_SELECTOR } from './constants';

/**
 * LinkDecorator appends the session parameter to outbound links.
 * It decorates existing links on init and uses a MutationObserver
 * to handle dynamically added links.
 */
export class LinkDecorator {
  readonly #paramName: string;
  readonly #selector: string;
  #paramValue: string;
  #observer: MutationObserver | null = null;

  constructor(paramName: string, sessionId: string, selector: string = DEFAULT_OUTLINK_SELECTOR) {
    this.#paramName = paramName;
    this.#paramValue = sessionId;
    this.#selector = selector;
  }

  /** Update the session ID used for decoration (e.g. after session renewal). */
  updateSessionId(id: string): void {
    this.#paramValue = id;
    this.decorateAll();
  }

  /** Decorate all matching links currently in the DOM. */
  decorateAll(): void {
    const links = document.querySelectorAll<HTMLAnchorElement>(this.#selector);
    links.forEach((link) => this.#decorateLink(link));
  }

  /** Start observing the DOM for dynamically added links. */
  observe(): void {
    if (this.#observer || typeof MutationObserver === 'undefined') return;

    this.#observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;

          // Check if the added node itself is a matching link
          if (node instanceof HTMLAnchorElement && node.matches(this.#selector)) {
            this.#decorateLink(node);
          }

          // Check descendants of the added node
          const nested = node.querySelectorAll<HTMLAnchorElement>(this.#selector);
          nested.forEach((link) => this.#decorateLink(link));
        }
      }
    });

    this.#observer.observe(document.body, { childList: true, subtree: true });
  }

  /** Stop the MutationObserver. */
  disconnect(): void {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #decorateLink(link: HTMLAnchorElement): void {
    try {
      const url = new URL(link.href);
      url.searchParams.set(this.#paramName, this.#paramValue);
      link.href = url.toString();
    } catch {
      // Invalid URL — skip silently
    }
  }
}
